import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface EmptyStateAction {
  label: string;
  icon?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  tooltip?: string;
}

interface Props {
  actionDisabled?: boolean;
  actionHidden?: boolean;
  title?: string;
  description?: React.ReactNode;
  supportingText?: React.ReactNode;
  primaryAction?: EmptyStateAction | null;
  onGoToWorkflow?: () => void;
  onAddSlides?: () => void;
}

const slideDrift = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const lightSweep = keyframes`
  0%, 68% {
    transform: translateX(-170%) skewX(-18deg);
    opacity: 0;
  }

  76% { opacity: 0.2; }

  88% {
    transform: translateX(190%) skewX(-18deg);
    opacity: 0.7;
  }

  100% {
    transform: translateX(190%) skewX(-18deg);
    opacity: 0;
  }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.92); }
  50% { opacity: 0.85; transform: scale(1); }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const motionSx = (animation: string) => ({
  animation,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

const SlideCard = ({ delay, wash, featured = false }: { delay: string; wash: number; featured?: boolean }) => (
  <Box
    className="study-slides-motion"
    sx={(theme) => ({
      width: featured ? 96 : 80,
      height: featured ? 124 : 106,
      overflow: 'hidden',
      borderRadius: 1.5,
      border: `1px solid ${
        theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.16) : theme.palette.divider
      }`,
      backgroundColor:
        theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.62) : theme.palette.grey[100],
      boxShadow:
        theme.palette.mode === 'dark' ? '0 18px 36px rgba(0, 0, 0, 0.38)' : '0 14px 28px rgba(15, 23, 42, 0.08)',
      ...motionSx(`${slideDrift} 6.8s ease-in-out ${delay} infinite`),
    })}
  >
    <Box sx={{ height: 16, backgroundColor: 'action.hover' }} />
    <Box
      sx={(theme) => ({
        position: 'relative',
        m: 1,
        height: featured ? 76 : 62,
        overflow: 'hidden',
        borderRadius: 1,
        background: `radial-gradient(circle at 32% 38%, ${alpha(theme.palette.primary.main, wash)} 0%, ${alpha(
          theme.palette.primary.main,
          0.04
        )} 72%)`,
        ...(featured
          ? {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: '42%',
                background: `linear-gradient(90deg, transparent, ${alpha(
                  theme.palette.common.white,
                  0.72
                )}, transparent)`,
                ...motionSx(`${lightSweep} 8s ease-in-out infinite`),
              },
            }
          : {}),
      })}
    />
  </Box>
);

const EMPTY_ANIMATION_WIDTH = 228;
const EMPTY_ANIMATION_HEIGHT = 156;

export const StudySlidesEmptyAnimation = ({ compact = false }: { compact?: boolean }) => {
  const scale = compact ? 0.64 : 1;

  return (
    <Box
      aria-hidden
      data-testid="study-slides-empty-animation"
      sx={{
        position: 'relative',
        width: compact ? EMPTY_ANIMATION_WIDTH * scale : EMPTY_ANIMATION_WIDTH,
        height: compact ? EMPTY_ANIMATION_HEIGHT * scale : EMPTY_ANIMATION_HEIGHT,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: EMPTY_ANIMATION_WIDTH,
          height: EMPTY_ANIMATION_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <Box
          className="study-slides-motion"
          sx={(theme) => ({
            position: 'absolute',
            top: 18,
            right: 24,
            left: 24,
            height: 120,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(
              theme.palette.primary.main,
              theme.palette.mode === 'dark' ? 0.2 : 0.3
            )} 0%, transparent 68%)`,
            ...motionSx(`${glowPulse} 6.8s ease-in-out infinite`),
          })}
        />
        <Box sx={{ position: 'absolute', top: 28, left: 14, transform: 'rotate(-9deg)' }}>
          <SlideCard delay="0s" wash={0.38} />
        </Box>
        <Box sx={{ position: 'absolute', top: 30, right: 12, transform: 'rotate(8deg)' }}>
          <SlideCard delay="1.1s" wash={0.3} />
        </Box>
        <Box sx={{ position: 'absolute', top: 6, left: '50%', zIndex: 1, transform: 'translateX(-50%)' }}>
          <SlideCard delay="0.45s" wash={0.55} featured />
        </Box>
      </Box>
    </Box>
  );
};

const StudySlidesEmptyState: React.FunctionComponent<Props> = ({
  actionDisabled = false,
  actionHidden = false,
  title = 'No slides in this study yet',
  description,
  supportingText,
  primaryAction,
  onGoToWorkflow,
  onAddSlides,
}) => {
  const resolvedDescription =
    description === undefined ? (actionHidden ? null : 'Add slides from your computer or Amazon S3.') : description;
  const resolvedPrimaryAction: EmptyStateAction | null =
    primaryAction === undefined
      ? actionHidden
        ? null
        : {
            label: 'Add slides',
            icon: <CloudUploadOutlinedIcon />,
            onClick: onAddSlides,
            disabled: actionDisabled,
          }
      : primaryAction;

  const primaryButton = resolvedPrimaryAction ? (
    <Tooltip
      title={
        resolvedPrimaryAction.disabled
          ? resolvedPrimaryAction.tooltip || 'This action is unavailable for this study.'
          : ''
      }
    >
      <span>
        {resolvedPrimaryAction.href ? (
          <Button
            component={RouterLink}
            to={resolvedPrimaryAction.href}
            variant="contained"
            color="primary"
            startIcon={resolvedPrimaryAction.icon}
            disabled={resolvedPrimaryAction.disabled}
            sx={motionSx(`${riseIn} 0.55s ease-out 0.12s both`)}
          >
            {resolvedPrimaryAction.label}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={resolvedPrimaryAction.icon}
            disabled={resolvedPrimaryAction.disabled}
            onClick={resolvedPrimaryAction.onClick}
            sx={motionSx(`${riseIn} 0.55s ease-out 0.12s both`)}
          >
            {resolvedPrimaryAction.label}
          </Button>
        )}
      </span>
    </Tooltip>
  ) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        minHeight: 400,
        p: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={4} sx={{ width: '100%', maxWidth: 640, alignItems: 'center', textAlign: 'center' }}>
        <StudySlidesEmptyAnimation />
        <Stack spacing={1.5} sx={motionSx(`${riseIn} 0.55s ease-out both`)}>
          <Typography variant="h4" fontWeight={600}>
            {title}
          </Typography>
          {resolvedDescription ? (
            <Typography variant="body1" color="text.secondary">
              {resolvedDescription}
            </Typography>
          ) : null}
          {supportingText ? (
            <Typography variant="body2" color="text.secondary">
              {supportingText}
            </Typography>
          ) : null}
        </Stack>
        {primaryButton || onGoToWorkflow ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            {primaryButton}
            {onGoToWorkflow ? (
              <Button color="secondary" variant="outlined" onClick={onGoToWorkflow}>
                Go to Workflow
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};

export default StudySlidesEmptyState;
